import { goto } from '$app/navigation';
import * as Sentry from '@sentry/browser';
import { AuthenticationService } from './AuthenticationService';
import { AccountService } from './AccountService';
import { ERROR_MESSAGES } from './AccountService';

export class QueryParameterHandler {
    private authService: AuthenticationService;
    private accountService: AccountService;

    constructor() {
        this.authService = AuthenticationService.getInstance();
        this.accountService = AccountService.getInstance();
    }

    async handleQueryParameters(): Promise<void> {
        const params = new URLSearchParams(window.location.search);

        if (params.has("logout")) {
            this.handleLogout();
        }

        if (params.has("target")) {
            this.handleTarget(params.get("target") || "");
        }

        if (params.has("error")) {
            this.handleError(params.get("error") || "");
        }

        if (params.has("code")) {
            await this.handleAuthCode(params.get("code") || "");
        }

        if (params.has("clearall")) {
            await this.handleClearAll();
        }
    }

    private handleLogout(): void {
        this.accountService.logOut();
    }

    private handleTarget(target: string): { shouldRedirectToLogin: boolean } {
        let decodedTarget;
        try {
            decodedTarget = JSON.parse(atob(decodeURIComponent(target)));
        } catch (e) {
            console.error(e);
            goto(window.location.pathname, { replaceState: true });
            Sentry.captureMessage("Failed to decode target", {
                extra: { target, error: e },
            });
            return { shouldRedirectToLogin: false };
        }

        if (!decodedTarget || !decodedTarget.expires || new Date(decodedTarget.expires) < new Date()) {
            goto(window.location.pathname, { replaceState: true });
            return { shouldRedirectToLogin: false };
        }

        // Create target cookie
        document.cookie = `target=${target}; expires=${new Date(Date.now() + 10 * 60 * 1000)}; path=/;`;
        return { shouldRedirectToLogin: true };
    }

    private handleError(error: string): string | null {
        if (error && ERROR_MESSAGES[error as keyof typeof ERROR_MESSAGES]) {
            const errorMessage = ERROR_MESSAGES[error as keyof typeof ERROR_MESSAGES];
            goto(window.location.pathname, { replaceState: true });
            return errorMessage;
        }
        return null;
    }

    private async handleAuthCode(code: string): Promise<{ success: boolean; error?: string }> {
        try {
            const jwt = await this.authService.logIn(code);
            window.history.replaceState({}, document.title, "/");
            // Note: The actual Firebase sign-in should be handled by the auth store
            return { success: true };
        } catch (e) {
            console.error(e);
            return { success: false, error: "Authentication failed" };
        }
    }

    private async handleClearAll(): Promise<void> {
        // Clear all cookies
        document.cookie.split(";").forEach((c) => {
            document.cookie = c
                .replace(/^ +/, "")
                .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });

        localStorage.clear();
        sessionStorage.clear();
        window.history.replaceState({}, document.title, "/");

        await this.accountService.logOut();
        if (window.bulmaAlert) {
            await window.bulmaAlert("All cookies, localstorage and sessionstorage cleared, try logging in again.");
        } else {
            alert("All cookies, localstorage and sessionstorage cleared, try logging in again.");
        }
    }
}
